<template>
	<div
		class="skeleton-block"
		:class="{ 'skeleton-block--btn': btn }"
		:style="blockStyle"
	>
		&nbsp;
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

/*
 *	Animated shimmer placeholder for loading states. Render inside a `v-else`
 *	(or conditional) branch so the animation stops when real content mounts.
 *
 *	Default height comes from the element's line-height (via `&nbsp;`) so
 *	applying a typography class (e.g. `.sgnl-paragraph-m-medium`) yields a
 *	placeholder that matches the real text's natural height — no pixel guessing.
 *	Pass `height` explicitly for chips, icons, images, etc. Pass `btn` for
 *	button-shaped placeholders (36px tall, matches v-btn default density).
 */
export default defineComponent({
	name: "SkeletonBlock",
	props: {
		width: { type: String, default: "100%" },
		height: { type: String, default: undefined },
		radius: { type: String, default: undefined },
		btn: { type: Boolean, default: false },
	},
	computed: {
		blockStyle(): Record<string, string> {
			const style: Record<string, string> = { width: this.width };
			if (this.height) style.height = this.height;
			if (this.radius) style.borderRadius = this.radius;
			return style;
		},
	},
});
</script>

<style scoped>
.skeleton-block {
	position: relative;
	background: rgba(var(--v-theme-on-surface), var(--v-border-opacity));
	border-radius: var(--sgnl-border-radius-6);
	overflow: hidden;
	color: transparent;
	flex-shrink: 0;
}

.skeleton-block::after {
	content: "";
	position: absolute;
	inset: 0;
	background: linear-gradient(
		90deg,
		rgba(var(--v-theme-surface), 0),
		rgba(var(--v-theme-surface), 0.3),
		rgba(var(--v-theme-surface), 0)
	);
	animation: skeleton-block-shimmer 1.5s infinite;
	transform: translateX(-100%);
}

.skeleton-block--btn {
	height: var(--sgnl-unit-36);
	border-radius: var(--sgnl-border-radius-6);
}

@keyframes skeleton-block-shimmer {
	100% {
		transform: translateX(100%);
	}
}
</style>
